import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container, Typography, Box, CircularProgress, Alert,
  Button, ToggleButtonGroup, ToggleButton, Pagination, Stack
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Add, ArrowBack, TrendingUp, AccessTime, Whatshot } from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import forumService from '../../services/forumService';
import ThreadListItem from '../../components/Forum/ThreadListItem';
import { useAuth } from '../../contexts/AuthContext';

const ForumCategory = () => {
  const theme = useTheme();
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState('new');

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await forumService.getThreads({ categorySlug, page, limit: 20, sort });
      if (res.success) {
        setThreads(res.data.threads);
        setTotalPages(res.data.pagination.pages);
      }
    } catch {
      setError('Nem sikerült betölteni a témákat.');
    } finally {
      setLoading(false);
    }
  }, [categorySlug, page, sort]);

  // Kategóriaváltáskor induljunk tiszta lappal (ne maradjon a régi fejléc / oldalszám)
  useEffect(() => {
    setCategory(null);
    setPage(1);
    const loadCategory = async () => {
      try {
        const catRes = await forumService.getCategories();
        if (catRes.success) {
          const cat = catRes.data.categories.find(c => c.slug === categorySlug);
          if (cat) setCategory(cat);
        }
      } catch { /* a fejléc nélkül is használható az oldal */ }
    };
    loadCategory();
  }, [categorySlug]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const handleNewThread = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/forum/uj-tema?category=${categorySlug}` } });
      return;
    }
    navigate(`/forum/uj-tema?category=${categorySlug}`);
  };

  const handlePageChange = (_, v) => {
    setPage(v);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortButtonSx = { flex: { xs: 1, sm: 'none' }, px: { xs: 1, sm: 2 }, py: 1, textTransform: 'none', gap: 0.5, minHeight: 44 };

  return (
    <>
      <Helmet>
        <title>{category?.name ?? 'Fórum'} – Wedigo Fórum</title>
      </Helmet>

      <Box sx={{ bgcolor: theme.palette.primary.dark, color: '#fff', py: { xs: 2.5, md: 3.5 }, px: 2 }}>
        <Container maxWidth="lg">
          <Button
            component={Link}
            to="/forum"
            startIcon={<ArrowBack />}
            sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) }, textTransform: 'none', mb: 1, ml: -1, minHeight: 40 }}
          >
            Fórum főoldal
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {category?.icon && <Typography sx={{ fontSize: { xs: 30, md: 36 }, lineHeight: 1 }}>{category.icon}</Typography>}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h1" fontFamily="Playfair Display, serif" fontWeight={700}
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, overflowWrap: 'anywhere' }}>
                {category?.name ?? '...'}
              </Typography>
              {category?.description && (
                <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.25, overflowWrap: 'anywhere' }}>{category.description}</Typography>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column-reverse', sm: 'row' }, mb: 2.5, gap: 1.5 }}>
          <ToggleButtonGroup
            value={sort}
            exclusive
            fullWidth
            onChange={(_, v) => { if (v) { setSort(v); setPage(1); } }}
            size="small"
            aria-label="Rendezés"
            sx={{ width: { sm: 'auto' } }}
          >
            <ToggleButton value="new" sx={sortButtonSx}><AccessTime fontSize="small" />Legújabb</ToggleButton>
            <ToggleButton value="active" sx={sortButtonSx}><Whatshot fontSize="small" />Aktív</ToggleButton>
            <ToggleButton value="top" sx={sortButtonSx}><TrendingUp fontSize="small" />Legjobb</ToggleButton>
          </ToggleButtonGroup>

          <Button variant="contained" startIcon={<Add />} onClick={handleNewThread} sx={{ minHeight: 44, px: 3 }}>
            Új téma
          </Button>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchThreads}>Újra</Button>}>
            {error}
          </Alert>
        )}

        {!loading && !error && threads.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center', borderRadius: '14px', bgcolor: 'background.paper', border: `1px dashed ${alpha(theme.palette.primary.main, 0.25)}` }}>
            <Typography color="text.secondary" gutterBottom>Még nincs téma ebben a kategóriában.</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleNewThread} sx={{ mt: 1 }}>
              Légy te az első!
            </Button>
          </Box>
        )}

        {!loading && !error && threads.length > 0 && (
          <Stack spacing={1.25}>
            {threads.map((thread) => (
              <ThreadListItem
                key={thread.id}
                thread={thread}
                showCategory={false}
                showScore
                showTags
                activity={sort === 'active'}
              />
            ))}
          </Stack>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" siblingCount={0} boundaryCount={1} />
          </Box>
        )}
      </Container>
    </>
  );
};

export default ForumCategory;
